import _ from 'lodash'
import type {CssValue} from '../../constants'
import {deserializeFunction, serializeFunction} from './function'
import {deserializeVariable, getVarValueRegex, serializeVariable} from './var'
import {decimalToHex, hexToDecimal} from '../../../../../utils/dataUtils'

const DEFAULT_COLOR: string = 'initial'
const COLOR_VAR_PREFIX: string = 'color'
const RGB_FUNCTION_NAME: string = 'rgb'
const RGBA_FUNCTION_NAME: string = 'rgba'

interface CssColor {
    value?: string
    alpha?: string
}

const colorThemeVarRegex = getVarValueRegex(COLOR_VAR_PREFIX)
const getColorValue = (value: CssColor['value']): string[] | undefined => {
    if (!value) {
        return [DEFAULT_COLOR]
    }

    if (colorThemeVarRegex.test(value)) {
        return [serializeVariable({variable: value})]
    }

    const [match, ...colors] = value.match(/^#?([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/) ?? []
    if (!match) {
        return undefined
    }

    return colors.map((hexColor: string) => hexToDecimal(hexColor).toString())
}

const getThemeVar = (arg: string): string | undefined => {
    const {variable, value, prefix, defaultValue} = deserializeVariable(arg) ?? {}
    if (!value || defaultValue || prefix !== COLOR_VAR_PREFIX || !/^[0-9]+$/.test(value)) {
        return
    }

    return variable
}

const getHexColor = (decimalColors: string[]): string | undefined => {
    const hexColors = []
    for (const decimalColor of decimalColors) {
        const colorNum = _.toNumber(decimalColor)
        if (_.isNaN(colorNum) || colorNum < 0 || colorNum > 255 || colorNum % 1 !== 0) {
            return undefined
        }

        hexColors.push(decimalToHex(colorNum))
    }

    return `#${hexColors.join('')}`
}

const getColor = (args: string[]): string | undefined => {
    if (args.length === 1) {
        const [arg] = args
        if (arg === DEFAULT_COLOR) {
            return DEFAULT_COLOR
        }

        const themeVar = getThemeVar(arg)
        if (themeVar) {
            return themeVar
        }
    } else {
        const hexColor = getHexColor(args)
        if (hexColor) {
            return hexColor
        }
    }
}

export const serializeColor = ({value, alpha}: CssColor): CssValue | undefined => {
    let name = RGB_FUNCTION_NAME
    const args = getColorValue(value)
    if (!args) {
        return undefined
    }

    if (alpha) {
        name = RGBA_FUNCTION_NAME
        args.push(alpha)
    }

    return serializeFunction({name, args})
}

export const deserializeColor = (val: CssValue): CssColor | undefined => {
    const func = deserializeFunction(val)
    if (!func) {
        return undefined
    }

    const {name, args} = func
    if ((name !== RGB_FUNCTION_NAME && name !== RGBA_FUNCTION_NAME) || args.length === 0) {
        return undefined
    }

    const color: CssColor = {}

    if (name === RGBA_FUNCTION_NAME) {
        const alphaString = args.pop()
        const alpha = _.toNumber(alphaString)
        if (_.isNaN(alpha) || alpha < 0 || alpha > 1) {
            return undefined
        }

        color.alpha = alphaString
    }

    if (args.length !== 1 && args.length !== 3) {
        return undefined
    }

    const value = getColor(args)
    if (!value) {
        return undefined
    }

    if (value !== DEFAULT_COLOR) {
        color.value = value
    }

    return color
}
